

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay } from '@mui/x-date-pickers';
import { Badge, Box, useMediaQuery } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useDispatch, useSelector } from 'react-redux';
import { CURRENT_DATE } from 'store/actions';

const events = [new Date('2024-01-01'), new Date('2024-01-02')];

const ServerDay = React.memo(function ServerDay({ highlightedDays = [], day, outsideCurrentMonth, ...other }) {
  const isSelected = !outsideCurrentMonth && highlightedDays.includes(day.getDate());
  const isDisabled = day > new Date();

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      sx={isSelected ? { 
        margin: '2px', 
        borderRadius: '50%',
        '& .MuiBadge-dot': {
          backgroundColor: '#36C5F0' // Sentinel cyan color instead of green
        }
      } : undefined}
      badgeContent={isSelected ? '' : undefined}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      variant="dot"
      color={isSelected ? 'default' : undefined}
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        disabled={isDisabled}
        sx={{
          '&.Mui-disabled': { color: 'text.disabled', opacity: 0.55 }
        }}
      />
    </Badge>
  );
});

const DatePickerStore = ({ sx }) => {
  const dateFromStore = useSelector((state) => state.customization.currentDate);
  const [calendar, setCalendar] = useState(new Date(dateFromStore || new Date()));
  const [highlightedDays, setHighlightedDays] = useState([]);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const pillRef = useRef(null);

  const updateHighlightedDays = useCallback((date) => {
    const daysToHighlight = events
      .filter((e) => e.getMonth() === date.getMonth() && e.getFullYear() === date.getFullYear())
      .map((e) => e.getDate());
    setHighlightedDays(daysToHighlight);
  }, []);

  useEffect(() => {
    updateHighlightedDays(calendar);
  }, [calendar, updateHighlightedDays]);

  const handleDateChange = (newValue) => {
    if (newValue && newValue <= new Date()) {
      setCalendar(newValue);
      updateHighlightedDays(newValue);
      dispatch({ type: CURRENT_DATE, currentDate: newValue.toISOString() });
    }
  };

  const pillText = new Date(calendar).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  // Pill styling
  const pillWidth = 177.6;
  const pillHeight = 40;
  const pillRadius = 11.2;
  const pillBorder = '0.48px solid rgba(207, 207, 207, 0.11)';
  const pillBg = 'linear-gradient(96deg, #050C28 0.29%, rgba(10, 14, 35, 0.49) 98.84%)';
  const textLight = '#EAF2FF';
  const divider = 'rgba(255,255,255,0.15)';
  const iconWidth = 44;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Pill trigger */}
      <Box
        ref={pillRef}
        onClick={() => setOpen(true)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: `${pillWidth}px`,
          height: `${pillHeight}px`,
          flexShrink: 0,
          borderRadius: `${pillRadius}px`,
          border: pillBorder,
          overflow: 'hidden',
          cursor: 'pointer',
          background: pillBg,
          // boxShadow: '0 6px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
          ...sx
        }}
      >
        {/* Icon on the LEFT */}
        <Box
          sx={{
            width: `${iconWidth}px`,
            height: '100%',
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(180deg, #091a31 0%, #08162a 100%)'
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: isSmall ? 17 : 18, color: textLight }} />
        </Box>

        {/* Divider */}
        <Box sx={{ alignSelf: 'stretch', borderLeft: `1px solid ${divider}` }} />

        {/* Date Text */}
        <Box
          sx={{
            flex: 1,
            color: textLight,
            fontWeight: 600,
            fontSize: '11.2px',
            lineHeight: 1,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            pl: 1.25
          }}
        >
          {pillText}
        </Box>
      </Box>

      {/* Hidden DatePicker for popup */}
     <DatePicker
  open={open}
  onClose={() => setOpen(false)}
  value={calendar}
  onChange={handleDateChange}
  slots={{ day: ServerDay }}
  slotProps={{
    day: { highlightedDays },

    // anchor for the popper
    popper: {
      anchorEl: () => pillRef.current,
      placement: 'bottom-end',
      modifiers: [{ name: 'offset', options: { offset: [0, 8] } }]
    },

    // Style for the popup container (desktop)
    desktopPaper: {
      sx: {
        background: 'linear-gradient(127deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)',
        color: '#EAF2FF',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.12)',
        // boxShadow: '0 12px 28px rgba(0,0,0,0.45)',
        '& .MuiPickersDay-today': {
          border: '1px solid #36C5F0 !important',
          color: '#36C5F0 !important'
        }
      }
    },

    // Style for mobile mode
    mobilePaper: {
      sx: {
        background: 'linear-gradient(127deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)',
        color: '#EAF2FF',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.12)',
        // boxShadow: '0 12px 28px rgba(0,0,0,0.45)',
        '& .MuiPickersDay-today': {
          border: '1px solid #36C5F0 !important',
          color: '#36C5F0 !important'
        }
      }
    },

    // Style for inner layout and days
   layout: {
  sx: {
    background: 'linear-gradient(127deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)',
    '& .MuiPickersCalendarHeader-root': {
      color: '#EAF2FF'
    },
    '& .MuiPickersFadeTransitionGroup-root': {
      color: '#EAF2FF'
    },
    // Make the left/right navigation arrows white
    '& .MuiPickersArrowSwitcher-button': {
      color: '#FFFFFF', // white arrows
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.12)', // subtle hover highlight
      },
    },
    '& .MuiDayCalendar-weekDayLabel': {
      color: 'rgba(234,242,255,0.75)'
    },
    '& .MuiPickersDay-root': {
      color: '#EAF2FF',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.15)'
      },
      '&.Mui-selected': {
        backgroundColor: '#36C5F0 !important', // Sentinel cyan for selected day
        color: '#fff !important',
        '&:hover': {
          backgroundColor: '#36C5F0 !important'
        }
      },
      '&.MuiPickersDay-today': {
        border: '1px solid #36C5F0 !important',
        color: '#36C5F0 !important',
        backgroundColor: 'transparent !important'
      },
      '&.Mui-disabled': {
        color: 'rgba(255,255,255,0.4)',
        opacity: 0.5
      }
    }
  }
}

  }}
  disablePortal
  sx={{
    position: 'absolute',
    width: 0,
    height: 0,
    overflow: 'hidden',
    opacity: 0
  }}
/>

    </LocalizationProvider>
  );
};

export default DatePickerStore;
